import _ from "lodash";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { fetchCategoryInfo } from "@/api/category";
import { fetchMainMenuList } from "@/api/main-menu";
import { useFetchCategoryData } from "repo/category";
import { useFetchSortOptions } from "repo/search";
import { useFetchWishListInfo } from "repo/wishlist";
import en from "@/localization/en.json";
import { getPaginationResultText } from "@/utils/pagination";
import { getWishlistUpdatedProducts } from "@/utils/helpers/wishlist";
import { structureBreadcrumbs } from "@/utils/helpers/structure-breadcrumbs.helpers";

import { useSort } from "hooks/useSort";
import { usePagination } from "hooks/usePagination";

import {
  FilterByBtn,
  SortByBtn,
  Breadcrumbs,
  BannerBgImage,
  CartSidebarWrapper,
  CategoriesMultiCardCarousel,
  SortModal,
  HtmlRenderer,
  FourOFour,
  NotFound,
  CustomSelect,
  Loader,
  AddToCartModal,
  Pagination,
  Card,
  MetaData,
  ProductFilters,
  FilterMobileView,
} from "@/components/index.js";

function Category({ categoryData }) {
  const router = useRouter();
  const { data: session } = useSession();
  const [categoryInfo, setCategoryInfo] = useState(categoryData);
  const [products, setProducts] = useState(categoryData?.products);
  const [attributeRowData, setAttributeRowData] = useState(
    categoryData?.attributes
  );
  const { getPaginateData } = usePagination();
  const { data: sortOptionData = [] } = useFetchSortOptions();
  const { sortData, setSelectedSortValue } = useSort(sortOptionData);

  const { isFetching } = useFetchCategoryData(router.asPath, {
    onSuccess: (data) => {
      setProducts(data.products);
      setCategoryInfo(data);
      setAttributeRowData(data.attributes);
    },
    enabled: Object.keys(router.query).length > 1,
  });

  const { refetch } = useFetchWishListInfo({
    enabled: false,
    onSuccess: (data) => {
      if (!data.length) return;
      if (!_.isEmpty(products)) {
        setProducts(getWishlistUpdatedProducts(data, products));
      }
    },
  });

  useEffect(() => {
    if (session?.token) {
      refetch();
    }
  }, [session]);

  if (!categoryInfo) return <FourOFour />;

  return (
    <>
      {isFetching && <Loader />}
      <MetaData {...categoryInfo.metadata} schema={categoryInfo.schema} />

      <div className="w-full sm:flex sm:justify-around pb-[1rem]">
        <div className="hidden sm:max-w-[15%] sm:w-[15%] lg:flex">
          <div className="sm:flex flex-col items-start w-full">
            <p className="text-grayD4 text-heading4 font-normal mt-[2rem]">
              {en["common.filter_by"]}
            </p>

            <div className="">
              <ProductFilters
                filterData={attributeRowData}
                prices={categoryInfo.prices}
              />
            </div>
          </div>
        </div>

        <div className="w-full sm:w-[65%] mt-[1.375rem] px-[1.25rem] md:px-0">
          <div className="">
            <Breadcrumbs
              breadcrumbs={structureBreadcrumbs(
                categoryInfo?.category?.full_path
              )}
              isCategoryPage
            />
          </div>

          <p
            className="text-lg font-medium mb-4"
            dangerouslySetInnerHTML={{
              __html: categoryInfo?.metadata.description,
            }}
          />

          <BannerBgImage
            className="aspect-[99/19] rounded-[0.438rem] sm:rounded-[0.75rem]"
            bgImageUrl={categoryInfo?.category.banner}
            imageOptions={{
              priority: true,
              style: "rounded-[0.75rem]",
            }}
          />

          <section className="mt-[1.875rem] mb-[3.938rem]">
            <div className="mb-[2.5rem]">
              <h1
                className="text-heading2 font-medium"
                dangerouslySetInnerHTML={{
                  __html: categoryInfo?.category?.name,
                }}
              ></h1>
            </div>
          </section>

          {!!categoryInfo?.category?.menus.length && (
            <section className="mt-[1.875rem] mb-[3.938rem]">
              <CategoriesMultiCardCarousel
                categories={categoryInfo?.category?.menus}
              />
            </section>
          )}

          <div className="lg:flex justify-between items-center mt-[1.875rem] sm:pb-3">
            <div className="space-x-[0.75rem] sm:space-x-0  sm:space-y-[0.625rem] flex items-center lg:block">
              <p className="text-[#414E5F] text-[0.75rem]">
                {getPaginationResultText(
                  categoryInfo?.meta,
                  en["common.results"]
                )}
              </p>
            </div>

            <div className="hidden lg:flex items-center">
              <label className="text-[#7B838F]">{en["common.sort_by"]}</label>
              <CustomSelect
                options={sortOptionData}
                onChange={(state) => sortData(state)}
                value={setSelectedSortValue}
              />
            </div>
          </div>

          {/* start mobile screen sort filter options */}
          <div className="pb-[2rem] pt-[3rem] lg:hidden">
            <div className="flex items-center justify-center space-x-[1rem]">
              <FilterByBtn />
              <SortByBtn />
            </div>
            <FilterMobileView
              filterData={attributeRowData}
              prices={categoryInfo.prices}
            />
            <SortModal
              sortData={sortOptionData}
              onChange={(data) => sortData({ value: data })}
              value={setSelectedSortValue}
            />
          </div>

          {/* end mobile screen sort filter options */}

          <div className="w-full mx-auto grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
            {products?.map((product) => (
              <Card product={product} key={product.slug} />
            ))}
          </div>

          {products && !products?.length && (
            <NotFound
              imageHolderClassName="w-[15rem] md:w-[20rem] h-[11.25rem] md:h-[15rem]"
              imageName="no-product-found.png"
              wrapperClassName="mx-auto mt-[4.375rem] mb-[8.43rem]"
              heading=""
              bodyText={en["common.no_product_found"]}
              bodyClassName="mt-[1.25rem] text-grayD3 text-body1 md:text-heading5 max-w-[23.5rem]"
            />
          )}
        </div>
      </div>
      <CartSidebarWrapper />

      {categoryInfo?.meta?.total > categoryInfo?.meta?.per_page && (
        <div className="mb-[5.625rem]">
          <Pagination
            total={Math.ceil(
              categoryInfo?.meta?.total / categoryInfo?.meta?.per_page
            )}
            currentPage={categoryInfo?.meta?.current_page - 1}
            getPaginateData={(data) => getPaginateData(data)}
          />
        </div>
      )}

      <div className="mt-[5rem] mb-[5.656rem] w-full px-7 lg:px-[7.5rem]">
        <HtmlRenderer
          htmlData={categoryInfo?.category?.description}
          visibleLineClass="line-clamp-none"
        />
      </div>
      <AddToCartModal />
    </>
  );
}

export async function getStaticPaths() {
  const { data } = await fetchMainMenuList();
  const { main_menu: mainMenu } = data;

  let paths = [];
  mainMenu.forEach((parentMenu) => {
    if (parentMenu.menus.length) {
      parentMenu.menus.forEach((childMenu) => {
        paths.push({
          params: { slug: childMenu.slug },
        });
      });
    }
    paths.push({
      params: { slug: parentMenu.slug },
    });
  });

  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  const data = await fetchCategoryInfo(`/categories/${params.slug}`);
  if (data) {
    return {
      props: {
        categoryData: data.data,
      },
    };
  }

  return {
    props: { categoryData: null },
  };
}

export default Category;
